<template>
  <div
    ref="triggerElement"
    class="relative inline-block"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot />
    <Transition
      :enter-active-class="`transition-opacity duration-200 ease-linear`"
      :leave-active-class="`transition-opacity duration-200 ease-linear`"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      mode="out-in"
    >
      <div
        v-if="show && isDesktop"
        ref="tooltipElement"
        class="fixed z-[9999] flex w-max min-w-[240px] flex-col justify-center rounded-lg bg-white text-gray-600 shadow-lg"
        :style="tooltipStyle"
        role="tooltip"
      >
        <slot name="content" />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useDefaultBreakpoints } from '#storefront-ui/composables'

const show = defineModel('show', {
  default: false,
  type: Boolean,
})

const emit = defineEmits<{
  clickOutside: [ev: MouseEvent]
}>()

const triggerElement = ref<HTMLElement>()
const tooltipElement = ref<HTMLElement>()

let hideTimeout: NodeJS.Timeout | null = null

onClickOutside(triggerElement, (event) => emit('clickOutside', event))

// Only show tooltip on desktop (lg breakpoint and above)
const { greaterOrEqual } = useDefaultBreakpoints()
const isDesktop = greaterOrEqual('lg')

const handleMouseEnter = () => {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  show.value = true
}

const handleMouseLeave = () => {
  hideTimeout = setTimeout(() => {
    show.value = false
  }, 100) // Small delay to allow moving mouse to tooltip
}
const tooltipStyle = computed(() => {
  if (!triggerElement.value || !show.value || !isDesktop.value) {
    return {}
  }

  const LEFT_OFFSET = 8
  const TOP_OFFSET = 8
  const rect = triggerElement.value.getBoundingClientRect()
  const tooltipHeight = 120

  const tooltipWidth =
    tooltipElement.value?.getBoundingClientRect().width || 240

  let top = rect.top - tooltipHeight - TOP_OFFSET
  let left = rect.left - tooltipWidth + rect.width

  if (top < 0) {
    top = rect.bottom + TOP_OFFSET
  }

  if (left < 0) {
    left = LEFT_OFFSET
  } else if (left + tooltipWidth > window.innerWidth) {
    left = window.innerWidth - tooltipWidth - LEFT_OFFSET
  }

  return {
    top: `${top}px`,
    left: `${left}px`,
  }
})
</script>
